import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle as AlertIcon,
  ArrowLeft as BackIcon,
  ChevronRight as ChevronIcon,
  RotateCcw as RetryIcon,
  Search as SearchIcon,
  UserSearch as UserSearchIcon,
} from "lucide-react";
import type { ContactDetail, ContactListItem } from "@multi-chat/shared";
import { LtrValue } from "../../components/common/LtrValue.js";
import { Button } from "../../components/ui/button/index.js";
import { Modal } from "../../components/ui/modal/index.js";
import { cn } from "../../lib/utils.js";
import { LinkedPlatformBadges } from "../contacts/LinkedPlatformBadges.js";
import { useContactSearch } from "../contacts/use-contact-search.js";
import { ContactChannelIdentityRow } from "./ContactChannelIdentityRow.js";
import { conversationAvatarTone } from "./conversation-avatar-tone.js";
import { contactChannelIdentities } from "./conversation-contact-identities.js";
import { conversationInitials } from "./inbox-display.js";
import { useSavedContactDetail } from "./use-saved-contact-detail.js";

/**
 * Finds a saved contact with the existing Contacts search and opens one of its existing
 * conversations. It never creates a conversation or sends anything.
 */
export function ContactLookupDialog({
  open,
  onOpenChange,
  organizationId,
  onOpenConversation,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
  onOpenConversation: (conversationId: number) => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("inbox.lookup.title")}
      description={t("inbox.lookup.description")}
      icon={<UserSearchIcon size={18} />}
      testId="inbox-contact-lookup-dialog"
      className="top-[72px] w-[min(540px,calc(100vw-2rem))] max-h-[calc(100dvh-88px)] translate-y-0"
      panelClassName="font-feedbot overflow-hidden rounded-[18px] border-0 shadow-[0_30px_90px_rgb(6_37_29/0.28)] text-inbox-text"
      bodyClassName="flex min-h-0 flex-col p-0"
    >
      <ContactLookupBody organizationId={organizationId} onOpenConversation={onOpenConversation} />
    </Modal>
  );
}

function ContactLookupBody({
  organizationId,
  onOpenConversation,
}: {
  organizationId: number;
  onOpenConversation: (conversationId: number) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<ContactListItem | null>(null);
  const search = useContactSearch(organizationId, query);
  const detail = useSavedContactDetail(organizationId, selected ? Number(selected.id) : null);
  const busy = search.state.kind === "searching" || (selected != null && detail.state.kind === "loading");

  function handleQueryChange(value: string): void {
    setQuery(value);
    setSelected(null);
  }

  let body: ReactNode;
  if (selected) {
    body = (
      <LookupContactDetail
        contact={selected}
        detail={detail.state.kind === "ready" ? detail.state.contact : null}
        failed={detail.state.kind === "error"}
        onRetry={detail.retry}
        onBack={() => setSelected(null)}
        onOpenConversation={onOpenConversation}
      />
    );
  } else {
    switch (search.state.kind) {
      case "idle":
        body = (
          <LookupMessage icon={<SearchIcon size={20} />} title={t("inbox.lookup.idleTitle")}>
            {t("inbox.lookup.idleHint")}
          </LookupMessage>
        );
        break;
      case "searching":
        body = (
          <>
            <span className="sr-only">{t("inbox.lookup.searching")}</span>
            {LOOKUP_SKELETON_WIDTHS.map((widths) => (
              <LookupRowSkeleton key={widths.name} widths={widths} />
            ))}
          </>
        );
        break;
      case "error":
        body = (
          <LookupMessage
            icon={<AlertIcon size={20} />}
            tone="error"
            title={t("inbox.lookup.errorTitle")}
            action={<RetryButton onClick={search.retry} />}
          >
            {t("inbox.lookup.errorBody")}
          </LookupMessage>
        );
        break;
      case "ready":
        body =
          search.state.results.length === 0 ? (
            <LookupMessage icon={<UserSearchIcon size={20} />} title={t("inbox.lookup.emptyTitle")}>
              {t("inbox.lookup.emptyBody", { query: query.trim() })}
            </LookupMessage>
          ) : (
            <>
              <p className="m-0 px-2.5 pt-1 pb-1.5 text-[0.71875rem] font-bold text-inbox-muted">
                {t("inbox.lookup.results", { count: search.state.results.length })}
              </p>
              <ul className="m-0 list-none p-0" data-testid="inbox-contact-lookup-results">
                {search.state.results.map((contact) => (
                  <li key={contact.id}>
                    <button
                      type="button"
                      className="flex min-h-14 w-full items-center gap-[11px] rounded-[10px] px-2.5 py-2 text-start hover:bg-inbox-hover focus-visible:bg-inbox-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
                      onClick={() => setSelected(contact)}
                    >
                      <ContactInitials contact={contact} />
                      <span className="min-w-0 flex-1">
                        <bdi className="block truncate text-[0.875rem] font-[650] text-inbox-strong">
                          {contact.displayName}
                        </bdi>
                        <ContactReachLine contact={contact} />
                      </span>
                      <LinkedPlatformBadges types={contact.linkedPlatformTypes} />
                      <ChevronIcon size={16} className="shrink-0 text-inbox-subtle rtl:-scale-x-100" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          );
        break;
      default: {
        const _exhaustive: never = search.state;
        body = _exhaustive;
      }
    }
  }

  return (
    <>
      <label className="mx-[18px] flex h-11 shrink-0 items-center gap-[9px] rounded-xl border border-[#d6e2dc] bg-surface px-3 transition-[border-color,box-shadow] duration-150 focus-within:border-primary focus-within:shadow-[0_0_0_3px_rgb(23_126_103/0.18)]">
        <span className="sr-only">{t("inbox.lookup.placeholder")}</span>
        <SearchIcon size={18} className="shrink-0 text-inbox-subtle" aria-hidden="true" />
        <input
          className="h-full min-w-0 flex-1 border-0 bg-transparent text-[0.90625rem] text-inbox-text outline-none placeholder:text-[#93a19d] focus:outline-none"
          value={query}
          dir="auto"
          autoComplete="off"
          spellCheck={false}
          autoFocus
          placeholder={t("inbox.lookup.placeholder")}
          onChange={(event) => handleQueryChange(event.target.value)}
          data-testid="inbox-contact-lookup-input"
        />
        {busy ? (
          <span
            className="size-4 shrink-0 animate-[spin_0.8s_linear_infinite] rounded-full border-2 border-inbox-line border-t-primary motion-reduce:animate-none"
            role="status"
            aria-label={t("inbox.lookup.searching")}
          />
        ) : null}
      </label>
      <div
        className="min-h-[260px] max-h-[430px] overflow-y-auto overscroll-contain px-2.5 pt-2.5 pb-3.5"
        aria-live="polite"
      >
        {body}
      </div>
    </>
  );
}

function LookupContactDetail({
  contact,
  detail,
  failed,
  onRetry,
  onBack,
  onOpenConversation,
}: {
  contact: ContactListItem;
  detail: ContactDetail | null;
  failed: boolean;
  onRetry: () => void;
  onBack: () => void;
  onOpenConversation: (conversationId: number) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const identities = detail ? contactChannelIdentities(detail.platformProfiles) : [];

  return (
    <div data-testid="inbox-contact-lookup-detail">
      <button
        type="button"
        className="mx-1 mt-0.5 mb-2 inline-flex items-center gap-1.5 rounded-[7px] px-1.5 py-1 text-[0.78125rem] font-[650] text-primary hover:bg-inbox-hover focus-visible:outline-2 focus-visible:outline-primary"
        onClick={onBack}
      >
        <BackIcon size={14} className="rtl:-scale-x-100" aria-hidden="true" />
        {t("inbox.lookup.back")}
      </button>
      <div className="flex items-center gap-3 px-2.5 pt-1 pb-3.5">
        <ContactInitials contact={contact} />
        <div className="min-w-0">
          <bdi className="block truncate text-[0.9375rem] font-semibold text-inbox-strong">{contact.displayName}</bdi>
          <ContactReachLine contact={contact} />
        </div>
      </div>
      <p className="m-0 px-2.5 pb-1 text-[0.75rem] font-bold text-inbox-muted">{t("inbox.lookup.profiles")}</p>
      {failed ? (
        <LookupMessage
          icon={<AlertIcon size={20} />}
          tone="error"
          title={t("inbox.lookup.detailErrorTitle")}
          action={<RetryButton onClick={onRetry} />}
        >
          {t("inbox.lookup.errorBody")}
        </LookupMessage>
      ) : detail == null ? (
        [0, 1].map((index) => (
          <div
            key={index}
            className="mx-2.5 my-1.5 flex items-center gap-[11px] rounded-xl border border-inbox-line px-3 py-2.5"
            aria-hidden="true"
          >
            <span className="size-7 animate-pulse rounded-full bg-inbox-line-soft motion-reduce:animate-none" />
            <span className="h-3 flex-1 animate-pulse rounded bg-inbox-line-soft motion-reduce:animate-none" />
          </div>
        ))
      ) : identities.length === 0 ? (
        <p className="m-0 px-2.5 py-2 text-[0.8125rem] text-inbox-muted">{t("inbox.lookup.noProfiles")}</p>
      ) : (
        <div className="px-2.5">
          {identities.map((identity) => (
            <ContactChannelIdentityRow
              key={identity.profileId}
              identity={identity}
              variant="lookup"
              onOpenConversation={onOpenConversation}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ContactInitials({ contact }: { contact: ContactListItem }): React.JSX.Element {
  return (
    <span
      className={cn(
        "grid size-9 shrink-0 place-items-center rounded-full text-[0.8125rem] font-[650]",
        conversationAvatarTone(Number(contact.id)),
      )}
      aria-hidden="true"
    >
      {conversationInitials(contact.displayName)}
    </span>
  );
}

function ContactReachLine({ contact }: { contact: ContactListItem }): React.JSX.Element | null {
  const parts = [contact.normalizedPhoneNumber, contact.email].filter((value): value is string => Boolean(value));
  if (parts.length === 0) return null;
  return (
    <LtrValue className="block truncate text-[0.75rem] text-inbox-muted tabular-nums">{parts.join(" · ")}</LtrValue>
  );
}

const LOOKUP_SKELETON_WIDTHS = [
  { name: "w-[70%]", detail: "w-1/2" },
  { name: "w-[55%]", detail: "w-[35%]" },
  { name: "w-[62%]", detail: "w-[42%]" },
] as const;

function LookupRowSkeleton({
  widths,
}: {
  widths: (typeof LOOKUP_SKELETON_WIDTHS)[number];
}): React.JSX.Element {
  const block = "animate-pulse rounded bg-inbox-line-soft motion-reduce:animate-none";
  return (
    <div className="flex min-h-14 items-center gap-[11px] px-2.5 py-2" aria-hidden="true">
      <span className={cn(block, "size-9 rounded-full")} />
      <span className="flex flex-1 flex-col gap-[7px]">
        <span className={cn(block, "h-[11px]", widths.name)} />
        <span className={cn(block, "h-[9px]", widths.detail)} />
      </span>
    </div>
  );
}

function LookupMessage({
  icon,
  title,
  children,
  action,
  tone = "neutral",
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  action?: ReactNode;
  tone?: "neutral" | "error";
}): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-1.5 px-6 py-10 text-center text-[0.8125rem] text-inbox-muted">
      <span
        className={cn(
          "mb-1.5 grid size-11 place-items-center rounded-xl",
          tone === "error" ? "bg-destructive/10 text-destructive" : "bg-[#f1f5f4] text-inbox-subtle",
        )}
        aria-hidden="true"
      >
        {icon}
      </span>
      <b className="text-[0.875rem] text-inbox-strong">{title}</b>
      <span>{children}</span>
      {action ? <div className="mt-2.5">{action}</div> : null}
    </div>
  );
}

function RetryButton({ onClick }: { onClick: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-8 gap-1.5 rounded-lg border-inbox-line text-[0.78125rem] text-inbox-strong"
      onClick={onClick}
    >
      <RetryIcon size={14} aria-hidden="true" />
      {t("inbox.retry")}
    </Button>
  );
}
